import { getStoredToken } from "./session";

function buildQueryString(params = {}) {
  const query = new URLSearchParams();

  Object.entries(params).forEach(([key, value]) => {
    if (value === undefined || value === null) {
      return;
    }

    const trimmedValue = typeof value === "string" ? value.trim() : value;

    if (trimmedValue === "" || Number.isNaN(trimmedValue)) {
      return;
    }

    query.append(key, trimmedValue);
  });

  const queryString = query.toString();
  return queryString ? `?${queryString}` : "";
}

function getTenantId() {
  const url = new URL(window.location.href);
  const match = url.hostname.match(
    /^admin-([a-z0-9-]+)\.megamatch\.(ai|local)$/
  );
  if (match && match[1]) {
    return match[1];
  }
  return "azizam"; // default tenant ID
}

function getBaseUrl() {
  return process.env.VUE_APP_BACKOFFICE_API_URL.replace(/\/$/, "").replace(
    "{tenantId}",
    getTenantId()
  );
}

export function getCdnUrl() {
  return process.env.VUE_APP_CDN_URL?.replace(/\/$/, "").replace(
    "{tenantId}",
    getTenantId()
  );
}

export function getMediaUrl(mediaPath) {
  if (!mediaPath) return null;
  const path = mediaPath.toString().replace(/^\//, "");
  const lower = path.toLowerCase();
  if (lower.includes("photo")) {
    return `${getCdnUrl()}/ugc/${path}.webp`;
  }
  if (lower.includes("voice")) {
    return `${getCdnUrl()}/ugc/${path}.mp3`;
  }
  return null;
}

async function parseResponseBody(response, responseType = "json") {
  if (responseType === "text") {
    return response.text();
  }
  if (responseType === "blob") {
    return response.blob();
  }
  const text = await response.text();
  if (!text) {
    return null;
  }

  try {
    return JSON.parse(text);
  } catch (error) {
    console.error("Failed to parse response JSON", error);
    return null;
  }
}

async function apiFetch(path, options = {}) {
  const {
    requiresAuth = true,
    headers: customHeaders = {},
    responseType = "json",
    ...restOptions
  } = options;

  const headers = {
    Accept: "application/json",
    ...customHeaders,
  };

  if (restOptions.body && !headers["Content-Type"]) {
    headers["Content-Type"] = "application/json";
  }

  if (requiresAuth) {
    const token = getStoredToken();
    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }
  }

  const response = await fetch(`${getBaseUrl()}${path}`, {
    ...restOptions,
    headers,
  });
  const payload = await parseResponseBody(response, responseType);

  if (!response.ok) {
    const errorMessage =
      (typeof payload === "string" && payload) ||
      payload?.message ||
      payload?.error ||
      "Request failed";
    const error = new Error(errorMessage);
    error.status = response.status;
    error.payload = payload;
    throw error;
  }

  return payload;
}

export function login(credentials) {
  return apiFetch("/auth/login", {
    method: "POST",
    body: JSON.stringify(credentials),
    requiresAuth: false,
  });
}

export function fetchAuthenticatedAdmin() {
  return apiFetch("/auth/me");
}

export function updateMyPreferredLanguage(preferredLanguage) {
  return apiFetch("/auth/me", {
    method: "POST",
    body: JSON.stringify({ preferredLanguage }),
  });
}

export function fetchBackofficeConfig() {
  return apiFetch("/config/tenant");
}

export function fetchAdmins({ page = 1, pageSize = 10, search } = {}) {
  const query = {
    page,
    pageSize,
  };

  if (typeof search === "string" && search.trim()) {
    query.search = search.trim();
  }

  return apiFetch(`/admin${buildQueryString(query)}`);
}

export function createAdmin(payload) {
  return apiFetch("/admin", {
    method: "POST",
    body: JSON.stringify(payload || {}),
  });
}

export function updateAdmin(id, payload) {
  return apiFetch(`/admin/${encodeURIComponent(id)}`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
  });
}

export function fetchAllConfigs() {
  return apiFetch("/config/all");
}

export function reloadPromptLibrary() {
  return apiFetch("/config/prompt-library/reload", { method: "POST" });
}

export function updateConfigValue(key, value) {
  return apiFetch(`/config/${key}`, {
    method: "POST",
    body: JSON.stringify({ value }),
  });
}

export function fetchPlans() {
  return apiFetch("/plan");
}

export function upsertPlan(id, payload) {
  if (!id) {
    return Promise.reject(new Error("Plan id is required"));
  }
  return apiFetch(`/plan/${encodeURIComponent(id)}`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
  });
}

export function fetchUsers({
  page = 1,
  pageSize = 20,
  search,
  sortBy,
  sortOrder,
  status,
  gender,
  verificationStatus,
} = {}) {
  const query = {
    page,
    pageSize,
  };

  if (typeof search === "string" && search.trim()) {
    query.search = search.trim();
  }
  if (sortBy) {
    query.sortBy = sortBy;
  }
  if (sortOrder) {
    query.sortOrder = sortOrder;
  }
  if (status) {
    query.status = status;
  }
  if (gender) {
    query.gender = gender;
  }
  if (verificationStatus) {
    query.verificationStatus = verificationStatus;
  }

  return apiFetch(`/user${buildQueryString(query)}`);
}

export function fetchUserById(id) {
  if (!id) {
    return Promise.reject(new Error("User id is required"));
  }
  return apiFetch(`/user/${id}`);
}

export function fetchReferral(search) {
  if (typeof search !== "string" || !search.trim()) {
    return Promise.reject(new Error("Search term is required"));
  }
  return apiFetch(`/growth/referral${buildQueryString({ search: search.trim() })}`);
}

export function fetchUserSessionToken(userId, sessionId) {
  if (!userId) {
    return Promise.reject(new Error("User id is required"));
  }
  if (!sessionId) {
    return Promise.reject(new Error("Session id is required"));
  }
  return apiFetch(
    `/user/${encodeURIComponent(userId)}/session/${encodeURIComponent(sessionId)}/get-token`
  );
}

export function fetchUserSessions(userId) {
  if (!userId) {
    return Promise.reject(new Error("User id is required"));
  }
  return apiFetch(`/user/${encodeURIComponent(userId)}/sessions`);
}

export function fetchUserDeviceMatches(userId) {
  if (!userId) {
    return Promise.reject(new Error("User id is required"));
  }
  return apiFetch(`/user/${encodeURIComponent(userId)}/device-matches`);
}

export function fetchUserMatchQuery(userId) {
  if (!userId) {
    return Promise.reject(new Error("User id is required"));
  }
  return apiFetch(`/user/${encodeURIComponent(userId)}/match-query`, {
    headers: { Accept: "text/plain" },
    responseType: "text",
  });
}

function appendCsvParam(query, key, value) {
  const list = Array.isArray(value) ? value : value ? [value] : [];
  const cleaned = list
    .map((item) => String(item ?? "").trim())
    .filter(Boolean);
  if (cleaned.length) {
    query.append(key, cleaned.join(","));
  }
}

export function fetchUsersAnalysisChart({
  x,
  xGranularity,
  y,
  yGranularity,
  dateFrom,
  dateTo,
  timezone,
  status,
  gender,
  countryCode,
  platform,
  activePlanId,
  ageGroup,
  minAge,
  maxAge,
  sortBy,
  sortDir,
} = {}) {
  const query = new URLSearchParams();
  if (x) query.append("x", x);
  if (xGranularity) query.append("xGranularity", xGranularity);
  if (y) query.append("y", y);
  if (yGranularity) query.append("yGranularity", yGranularity);
  if (dateFrom) query.append("dateFrom", dateFrom);
  if (dateTo) query.append("dateTo", dateTo);
  if (timezone) query.append("timezone", timezone);
  appendCsvParam(query, "status", status);
  appendCsvParam(query, "gender", gender);
  appendCsvParam(query, "countryCode", countryCode);
  appendCsvParam(query, "platform", platform);
  appendCsvParam(query, "activePlanId", activePlanId);
  appendCsvParam(query, "ageGroup", ageGroup);
  if (minAge !== undefined && minAge !== null && minAge !== "") {
    query.append("minAge", minAge);
  }
  if (maxAge !== undefined && maxAge !== null && maxAge !== "") {
    query.append("maxAge", maxAge);
  }
  if (sortBy) query.append("sortBy", sortBy);
  if (sortDir) query.append("sortDir", sortDir);

  const queryString = query.toString();
  return apiFetch(`/analysis/users/chart${queryString ? `?${queryString}` : ""}`);
}

export function fetchPlansAnalysisReport({
  granularity,
  dimension,
  walletDimension,
  includeSpent,
  dateFrom,
  dateTo,
  timezone,
  planId,
  platform,
  status,
  gender,
  userPlanCountry,
  addressCountry,
  ageGroup,
  minAge,
  maxAge,
  signInBy,
  verificationStatus,
} = {}) {
  const query = new URLSearchParams();
  if (granularity) query.append("granularity", granularity);
  if (dimension) query.append("dimension", dimension);
  if (walletDimension) query.append("walletDimension", walletDimension);
  if (includeSpent) query.append("includeSpent", "true");
  if (dateFrom) query.append("dateFrom", dateFrom);
  if (dateTo) query.append("dateTo", dateTo);
  if (timezone) query.append("timezone", timezone);
  appendCsvParam(query, "planId", planId);
  appendCsvParam(query, "platform", platform);
  appendCsvParam(query, "status", status);
  appendCsvParam(query, "gender", gender);
  appendCsvParam(query, "userPlanCountry", userPlanCountry);
  appendCsvParam(query, "addressCountry", addressCountry);
  appendCsvParam(query, "ageGroup", ageGroup);
  if (minAge !== undefined && minAge !== null && minAge !== "") {
    query.append("minAge", minAge);
  }
  if (maxAge !== undefined && maxAge !== null && maxAge !== "") {
    query.append("maxAge", maxAge);
  }
  appendCsvParam(query, "signInBy", signInBy);
  appendCsvParam(query, "verificationStatus", verificationStatus);

  const queryString = query.toString();
  return apiFetch(`/analysis/plans/report${queryString ? `?${queryString}` : ""}`);
}

export function fetchComplaintsAnalysisChart({
  dimension,
  granularity,
  dateFrom,
  dateTo,
  timezone,
} = {}) {
  const query = new URLSearchParams();
  if (dimension) query.append("dimension", dimension);
  if (granularity) query.append("granularity", granularity);
  if (dateFrom) query.append("dateFrom", dateFrom);
  if (dateTo) query.append("dateTo", dateTo);
  if (timezone) query.append("timezone", timezone);

  const queryString = query.toString();
  return apiFetch(`/analysis/complaints/chart${queryString ? `?${queryString}` : ""}`);
}

export function fetchMessagesAnalysisChart({
  dimension,
  granularity,
  dateFrom,
  dateTo,
  timezone,
} = {}) {
  const query = new URLSearchParams();
  if (dimension) query.append("dimension", dimension);
  if (granularity) query.append("granularity", granularity);
  if (dateFrom) query.append("dateFrom", dateFrom);
  if (dateTo) query.append("dateTo", dateTo);
  if (timezone) query.append("timezone", timezone);

  const queryString = query.toString();
  return apiFetch(`/analysis/messages/chart${queryString ? `?${queryString}` : ""}`);
}

export function fetchRelationsAnalysisChart({
  dimension,
  granularity,
  dateFrom,
  dateTo,
  timezone,
} = {}) {
  const query = new URLSearchParams();
  if (dimension) query.append("dimension", dimension);
  if (granularity) query.append("granularity", granularity);
  if (dateFrom) query.append("dateFrom", dateFrom);
  if (dateTo) query.append("dateTo", dateTo);
  if (timezone) query.append("timezone", timezone);

  const queryString = query.toString();
  return apiFetch(`/analysis/relations/chart${queryString ? `?${queryString}` : ""}`);
}

export function fetchAllVerificationAttempts({ page = 1, pageSize = 25 } = {}) {
  return apiFetch(`/verification/attempts${buildQueryString({ page, pageSize })}`);
}

export function fetchVerificationAttempts(userId) {
  if (!userId) {
    return Promise.reject(new Error("User id is required"));
  }
  return apiFetch(`/verification/user/${userId}`);
}

export function fetchVerificationMedia(attemptId, index) {
  if (!attemptId && attemptId !== 0) {
    return Promise.reject(new Error("Attempt id is required"));
  }
  if (typeof index !== "number" || index < 0) {
    return Promise.reject(
      new Error("Media index must be a non-negative number")
    );
  }
  return apiFetch(`/verification/${attemptId}/media/${index}`).then(
    (payload) => {
      if (!payload || !payload.content) {
        throw new Error("Empty media response");
      }
      const contentType =
        payload.contentType ||
        payload.content_type ||
        "application/octet-stream";
      const binary = atob(payload.content);
      const bytes = new Uint8Array(binary.length);
      for (let i = 0; i < binary.length; i += 1) {
        bytes[i] = binary.charCodeAt(i);
      }
      const blob = new Blob([bytes], { type: contentType });
      return {
        blob,
        contentType,
        contentLength: payload.contentLength ?? payload.content_length,
      };
    }
  );
}

export function updateVerificationAttemptStatus(attemptId, status) {
  if (!attemptId) {
    return Promise.reject(new Error("Attempt id is required"));
  }
  if (!status) {
    return Promise.reject(new Error("Status is required"));
  }
  return apiFetch(`/verification/${attemptId}/status/${status}`, {
    method: "POST",
  });
}

export function rerunVerificationAttemptAi(attemptId) {
  if (!attemptId) {
    return Promise.reject(new Error("Attempt id is required"));
  }
  return apiFetch(`/verification/${attemptId}/rerun-ai`, {
    method: "POST",
  });
}

export function fetchEmailTemplates() {
  return apiFetch("/email-template");
}

export function updateEmailTemplate(payload) {
  const { name, language, subject, htmlBody, textBody } = payload || {};
  if (!name || !language) {
    return Promise.reject(new Error("Template name and language are required"));
  }
  return apiFetch("/email-template", {
    method: "POST",
    body: JSON.stringify({ name, language, subject, htmlBody, textBody }),
  });
}

export function fetchProfileAttributes() {
  return apiFetch("/profile-attribute");
}

export function updateProfileAttribute(name, payload) {
  if (!name) {
    return Promise.reject(new Error("Attribute name is required"));
  }
  return apiFetch(`/profile-attribute/${name}`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
  });
}

export function updateUser(id, payload) {
  if (!id) {
    return Promise.reject(new Error("User id is required"));
  }
  return apiFetch(`/user/${id}`, {
    method: "PATCH",
    body: JSON.stringify(payload || {}),
  });
}

export function updateUserStatus(id, status, reason, references = []) {
  if (!id) {
    return Promise.reject(new Error("User id is required"));
  }
  if (!status) {
    return Promise.reject(new Error("Status is required"));
  }
  if (!reason || !`${reason}`.trim()) {
    return Promise.reject(new Error("A reason is required to change a user status"));
  }
  return apiFetch(`/user/${id}/status`, {
    method: "POST",
    body: JSON.stringify({
      status,
      reason: `${reason}`.trim(),
      references: normalizeActivityReferences(references),
    }),
  });
}

export function normalizeActivityReferences(references) {
  if (!Array.isArray(references)) return [];
  return references
    .filter((reference) => reference?.type && reference?.id)
    .map((reference) => ({ type: reference.type, id: `${reference.id}` }));
}

export function fetchUserActivityLog(id, { page = 1, pageSize = 25 } = {}) {
  if (!id) {
    return Promise.reject(new Error("User id is required"));
  }
  const query = new URLSearchParams({
    page: String(page),
    pageSize: String(pageSize),
  });
  return apiFetch(`/user/${encodeURIComponent(id)}/activity-log?${query.toString()}`);
}

export function deleteUser(id, reason) {
  return updateUserStatus(id, "deleted", reason);
}

export function markAtomInappropriateContent(userId, atomId) {
  if (!userId) {
    return Promise.reject(new Error("User id is required"));
  }
  if (!atomId) {
    return Promise.reject(new Error("Atom id is required"));
  }
  return apiFetch(`/user/${encodeURIComponent(userId)}/atom/inappropriate-content`, {
    method: "POST",
    body: JSON.stringify({ atomId }),
  });
}

export function unmarkAtomInappropriateContent(userId, atomId) {
  if (!userId) {
    return Promise.reject(new Error("User id is required"));
  }
  if (!atomId) {
    return Promise.reject(new Error("Atom id is required"));
  }
  return apiFetch(`/user/${encodeURIComponent(userId)}/atom/inappropriate-content`, {
    method: "DELETE",
    body: JSON.stringify({ atomId }),
  });
}

export function sendUserWarningMessage(id, complaintIds = [], reason, references = []) {
  if (!id) {
    return Promise.reject(new Error("User id is required"));
  }
  if (!reason || !`${reason}`.trim()) {
    return Promise.reject(new Error("A reason is required to send a warning message"));
  }
  return apiFetch(`/user/${id}/send-warning-message`, {
    method: "POST",
    body: JSON.stringify({
      complaintIds,
      reason: `${reason}`.trim(),
      references: normalizeActivityReferences(references),
    }),
  });
}

export function fetchUserPurchases({ userId, page = 1, pageSize = 25 } = {}) {
  const query = new URLSearchParams();
  if (page) query.append("page", page);
  if (pageSize) query.append("pageSize", pageSize);
  const queryString = query.toString();
  return apiFetch(`/user/${userId}/purchase${queryString ? `?${queryString}` : ""}`);
}

export function fetchUserPlanUsage({ userId } = {}) {
  if (!userId) return Promise.reject(new Error("User id is required"));
  return apiFetch(`/user/${userId}/purchase/usage`);
}

export function setUserPlanFeatureUsage(userId, planId, payableFeature, usageCount) {
  if (!userId) return Promise.reject(new Error("User id is required"));
  if (!planId) return Promise.reject(new Error("Plan id is required"));
  if (!payableFeature) return Promise.reject(new Error("Feature is required"));
  return apiFetch(
    `/user/${encodeURIComponent(userId)}/plan/${encodeURIComponent(planId)}/usage/${encodeURIComponent(payableFeature)}`,
    {
      method: "PATCH",
      body: JSON.stringify({ usageCount }),
    }
  );
}

export function fetchUserWallet({ userId } = {}) {
  if (!userId) return Promise.reject(new Error("User id is required"));
  return apiFetch(`/user/${userId}/wallet`);
}

export function addWalletTransaction(userId, { amount, type, description } = {}) {
  if (!userId) return Promise.reject(new Error("User id is required"));
  return apiFetch(`/user/${userId}/wallet/transaction`, {
    method: "POST",
    body: JSON.stringify({ amount, type, description }),
  });
}

export function fetchUtopiaBalance() {
  return apiFetch("/utopia/balance");
}

export function fetchUserMedia({ userId, page = 1, pageSize = 25 } = {}) {
  if (!userId) {
    return Promise.reject(new Error("User id is required"));
  }
  const query = new URLSearchParams();
  if (page) query.append("page", page);
  if (pageSize) query.append("pageSize", pageSize);
  const queryString = query.toString();
  return apiFetch(
    `/user/${encodeURIComponent(userId)}/media${queryString ? `?${queryString}` : ""}`
  );
}

export function addMediaLabel(mediaPath, label, { value, score } = {}) {
  if (!mediaPath) {
    return Promise.reject(new Error("Media path is required"));
  }
  if (!label) {
    return Promise.reject(new Error("Label is required"));
  }
  const encodedPath = mediaPath.split("/").map(encodeURIComponent).join("/");
  const body = {};
  if (value != null) body.value = value;
  if (score != null) body.score = score;
  return apiFetch(`/media/${encodedPath}/label/${label}`, {
    method: "POST",
    body: Object.keys(body).length > 0 ? JSON.stringify(body) : undefined,
  });
}

export function findSimilarMedia(mediaPath, { threshold, limit } = {}) {
  if (!mediaPath) {
    return Promise.reject(new Error("Media path is required"));
  }
  const query = new URLSearchParams();
  if (threshold != null) query.append("threshold", threshold);
  if (limit != null) query.append("limit", limit);
  const queryString = query.toString();
  const encodedPath = mediaPath.split("/").map(encodeURIComponent).join("/");
  return apiFetch(
    `/media/${encodedPath}/similar${queryString ? `?${queryString}` : ""}`
  );
}

export function deleteMediaLabel(mediaPath, label, value) {
  if (!mediaPath) {
    return Promise.reject(new Error("Media path is required"));
  }
  if (!label) {
    return Promise.reject(new Error("Label is required"));
  }
  const query = new URLSearchParams();
  if (value != null) query.append("value", value);
  const encodedPath = mediaPath.split("/").map(encodeURIComponent).join("/");
  return apiFetch(`/media/${encodedPath}/label/${label}${query.toString()}`, {
    method: "DELETE",
  });
}

export function fetchRefreshUserPurchases(userId) {
  if (!userId) {
    return Promise.reject(new Error("User id is required"));
  }
  return apiFetch(`/user/${userId}/purchase/refresh`, {
    method: "POST",
  });
}

export function updateUserPurchasePeriod(userId, purchaseId, { fromDatetime, toDatetime } = {}) {
  if (!userId) {
    return Promise.reject(new Error("User id is required"));
  }
  if (!purchaseId) {
    return Promise.reject(new Error("Purchase id is required"));
  }
  return apiFetch(`/user/${userId}/purchase/${purchaseId}`, {
    method: "PATCH",
    body: JSON.stringify({
      fromDatetime: fromDatetime || null,
      toDatetime: toDatetime || null,
    }),
  });
}

export function transferUserPlan(userId, purchaseId, destinationUserId) {
  if (!userId) {
    return Promise.reject(new Error("User id is required"));
  }
  if (!purchaseId) {
    return Promise.reject(new Error("Purchase id is required"));
  }
  return apiFetch(`/user/${userId}/purchase/${purchaseId}/transfer`, {
    method: "POST",
    body: JSON.stringify({ destinationUserId }),
  });
}

export function pushUserClientEvents(userId) {
  if (!userId) {
    return Promise.reject(new Error("User id is required"));
  }
  return apiFetch(`/user/${userId}/push-client-events`, {
    method: "POST",
  });
}

export function fetchComplaints({
  page = 1,
  pageSize = 20,
  status,
  groupBy,
  sortBy,
  assignedAdminIds,
} = {}) {
  const query = new URLSearchParams();
  query.append("page", page);
  query.append("pageSize", pageSize);
  if (status) query.append("status", status);
  if (groupBy) query.append("groupBy", groupBy);
  if (sortBy) query.append("sortBy", sortBy);
  if (Array.isArray(assignedAdminIds)) {
    assignedAdminIds.forEach((adminId) => {
      query.append("adminId", adminId === null ? "null" : adminId);
    });
  }
  const queryString = query.toString();
  return apiFetch(`/complaint${queryString ? `?${queryString}` : ""}`);
}

export function fetchComplaintsByUser(userId, { groupBy } = {}) {
  if (!userId) {
    return Promise.reject(new Error("User id is required"));
  }
  const segment = groupBy === "reporterUserId" ? "reporter" : "reported";
  return apiFetch(`/complaint/${segment}/${encodeURIComponent(userId)}`);
}

export function fetchUserRelations({
  fromUser,
  toUser,
  userIds = [],
  type,
  page = 1,
  pageSize = 25,
} = {}) {
  const query = new URLSearchParams();
  const normalizedFromUser =
    typeof fromUser === "string" ? fromUser.trim() : fromUser;
  const normalizedToUser = typeof toUser === "string" ? toUser.trim() : toUser;

  if (normalizedFromUser) query.append("fromUser", normalizedFromUser);
  if (normalizedToUser) query.append("toUser", normalizedToUser);

  const list = Array.isArray(userIds) ? userIds : [userIds];
  list.forEach((value) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    if (trimmed) {
      query.append("userIds[]", trimmed);
    }
  });

  if (type) query.append("type", type);
  if (page) query.append("page", page);
  if (pageSize) query.append("pageSize", pageSize);

  const queryString = query.toString();
  return apiFetch(`/user/relation${queryString ? `?${queryString}` : ""}`);
}

export function updateComplaintStatus(id, status, comment) {
  if (!id) {
    return Promise.reject(new Error("Complaint id is required"));
  }
  const trimmedComment = typeof comment === "string" ? comment.trim() : "";
  return apiFetch(`/complaint/${id}/status`, {
    method: "POST",
    body: JSON.stringify({
      status,
      ...(trimmedComment ? { comment: trimmedComment } : {}),
    }),
  });
}

export function setComplaintAssignedAdmin(id, adminId) {
  if (!id) {
    return Promise.reject(new Error("Complaint id is required"));
  }
  return apiFetch(`/complaint/${id}/assign`, {
    method: "POST",
    body: JSON.stringify({ adminId: adminId || null }),
  });
}

export function rerunComplaintAi(id) {
  if (!id) {
    return Promise.reject(new Error("Complaint id is required"));
  }
  return apiFetch(`/complaint/${id}/rerun-ai`, {
    method: "POST",
  });
}

export function fetchChatContacts({
  participantIds = [],
  page = 1,
  pageSize = 20,
} = {}) {
  const query = new URLSearchParams();
  const ids = Array.isArray(participantIds) ? participantIds : [participantIds];
  ids.forEach((value) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    if (trimmed) {
      query.append("participantIds[]", trimmed);
    }
  });
  if (page) query.append("page", page);
  if (pageSize) query.append("pageSize", pageSize);
  const queryString = query.toString();
  return apiFetch(`/chat/contacts${queryString ? `?${queryString}` : ""}`);
}

export function fetchChatMessages({
  participantIds = [],
  page = 1,
  pageSize = 30,
} = {}) {
  const ids = Array.isArray(participantIds) ? participantIds : [participantIds];
  if (ids.length !== 2) {
    return Promise.reject(
      new Error("participantIds must contain exactly 2 items")
    );
  }
  const query = new URLSearchParams();
  ids.forEach((value) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    if (trimmed) {
      query.append("participantIds[]", trimmed);
    }
  });
  if (page) query.append("page", page);
  if (pageSize) query.append("pageSize", pageSize);
  const queryString = query.toString();
  return apiFetch(`/chat/messages${queryString ? `?${queryString}` : ""}`);
}

export function fetchChatMessageById(id) {
  const trimmed = typeof id === "string" ? id.trim() : id ? `${id}` : "";
  if (!trimmed) {
    return Promise.reject(new Error("id is required"));
  }
  return apiFetch(`/chat/messages/${trimmed}`);
}

export function fetchChatMessagesTranslation(messageIds, targetLanguage) {
  const ids = Array.isArray(messageIds) ? messageIds : [messageIds];
  if (ids.length === 0) {
    return Promise.reject(new Error("messageIds is required"));
  }
  if (!targetLanguage) {
    return Promise.reject(new Error("Target language is required"));
  }
  const query = new URLSearchParams();
  ids.forEach((value) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    if (trimmed) {
      query.append("messageIds[]", trimmed);
    }
  });
  query.append("targetLanguage", targetLanguage);
  return apiFetch(`/translation/chat-messages?${query.toString()}`);
}

export function fetchFreeTextTranslation(texts, targetLanguage) {
  const list = Array.isArray(texts) ? texts : [texts];
  if (list.length === 0) {
    return Promise.reject(new Error("texts is required"));
  }
  if (!targetLanguage) {
    return Promise.reject(new Error("Target language is required"));
  }
  const query = new URLSearchParams();
  list.forEach((value) => {
    if (typeof value === "string" && value.trim()) {
      query.append("texts[]", value);
    }
  });
  query.append("targetLanguage", targetLanguage);
  return apiFetch(`/translation/free-text?${query.toString()}`);
}

export function fetchComplianceRules() {
  return apiFetch("/compliance-rule");
}

export function fetchComplianceRuleById(id) {
  if (!id) {
    return Promise.reject(new Error("Rule id is required"));
  }
  return apiFetch(`/compliance-rule/${encodeURIComponent(id)}`);
}

export function createComplianceRule(payload) {
  return apiFetch("/compliance-rule", {
    method: "PUT",
    body: JSON.stringify(payload || {}),
  });
}

export function updateComplianceRule(id, payload) {
  if (!id) {
    return Promise.reject(new Error("Rule id is required"));
  }
  return apiFetch(`/compliance-rule/${encodeURIComponent(id)}`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
  });
}

export function deleteComplianceRule(id) {
  if (!id) {
    return Promise.reject(new Error("Rule id is required"));
  }
  return apiFetch(`/compliance-rule/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function fetchMediaComplianceRules() {
  return apiFetch("/media-compliance-rule");
}

export function fetchMediaComplianceRuleById(id) {
  if (!id) {
    return Promise.reject(new Error("Rule id is required"));
  }
  return apiFetch(`/media-compliance-rule/${encodeURIComponent(id)}`);
}

export function createMediaComplianceRule(payload) {
  return apiFetch("/media-compliance-rule", {
    method: "PUT",
    body: JSON.stringify(payload || {}),
  });
}

export function updateMediaComplianceRule(id, payload) {
  if (!id) {
    return Promise.reject(new Error("Rule id is required"));
  }
  return apiFetch(`/media-compliance-rule/${encodeURIComponent(id)}`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
  });
}

export function addMediaComplianceRuleMedia(id, media) {
  if (!id) {
    return Promise.reject(new Error("Rule id is required"));
  }
  return apiFetch(`/media-compliance-rule/${encodeURIComponent(id)}/media`, {
    method: "POST",
    body: JSON.stringify({ media: media || [] }),
  });
}

export function deleteMediaComplianceRule(id) {
  if (!id) {
    return Promise.reject(new Error("Rule id is required"));
  }
  return apiFetch(`/media-compliance-rule/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function vectorizeMediaComplianceRuleImage(file) {
  if (!file) {
    return Promise.reject(new Error("An image file is required"));
  }
  return apiFetch("/media-compliance-rule/vectorize", {
    method: "POST",
    headers: { "Content-Type": file.type || "application/octet-stream" },
    body: file,
  });
}

export function vectorizeMediaComplianceRuleImageFromMediaPath(mediaPath) {
  if (!mediaPath) {
    return Promise.reject(new Error("Media path is required"));
  }
  return apiFetch("/media-compliance-rule/vectorize-from-media", {
    method: "POST",
    body: JSON.stringify({ mediaPath }),
  });
}

export function fetchMediaComplianceRuleReferenceImage(imagePath) {
  if (!imagePath) {
    return Promise.reject(new Error("Image path is required"));
  }
  const encodedPath = imagePath.split("/").map(encodeURIComponent).join("/");
  return apiFetch(`/media-compliance-rule/reference-image/${encodedPath}`, {
    responseType: "blob",
  });
}

export function applyMediaComplianceRuleToExisting(id) {
  if (!id) {
    return Promise.reject(new Error("Rule id is required"));
  }
  return apiFetch(
    `/media-compliance-rule/${encodeURIComponent(id)}/apply-to-existing`,
    { method: "POST" }
  );
}

export function previewMediaComplianceRuleMatches(
  vectors,
  { page, pageSize } = {}
) {
  if (!Array.isArray(vectors) || !vectors.length) {
    return Promise.reject(new Error("At least one reference vector is required"));
  }
  const query = new URLSearchParams();
  if (page != null) query.append("page", page);
  if (pageSize != null) query.append("pageSize", pageSize);
  const queryString = query.toString();
  return apiFetch(
    `/media-compliance-rule/preview${queryString ? `?${queryString}` : ""}`,
    {
      method: "POST",
      body: JSON.stringify({ vectors }),
    }
  );
}

export function fetchAdCreatives({ page = 1, pageSize = 25, enabled } = {}) {
  const query = { page, pageSize };
  if (enabled === true || enabled === false) {
    query.enabled = enabled;
  }
  return apiFetch(`/ads/creative${buildQueryString(query)}`);
}

export function fetchAdCreativeById(id) {
  if (!id) {
    return Promise.reject(new Error("Ad creative id is required"));
  }
  return apiFetch(`/ads/creative/${encodeURIComponent(id)}`);
}

export function fetchAdCreativeStats(
  id,
  { dateFrom, dateTo, timezone, granularity, compare } = {},
) {
  if (!id) {
    return Promise.reject(new Error("Ad creative id is required"));
  }
  return apiFetch(
    `/ads/creative/${encodeURIComponent(id)}/stats${buildQueryString({
      dateFrom,
      dateTo,
      timezone,
      granularity,
      compare,
    })}`,
  );
}

export function createAdCreative(payload) {
  return apiFetch("/ads/creative", {
    method: "PUT",
    body: JSON.stringify(payload || {}),
  });
}

export function updateAdCreative(id, payload) {
  if (!id) {
    return Promise.reject(new Error("Ad creative id is required"));
  }
  return apiFetch(`/ads/creative/${encodeURIComponent(id)}`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
  });
}

export function deleteAdCreative(id) {
  if (!id) {
    return Promise.reject(new Error("Ad creative id is required"));
  }
  return apiFetch(`/ads/creative/${encodeURIComponent(id)}`, {
    method: "DELETE",
  });
}

export function getSupportSseUrl() {
  return getBaseUrl().concat("/support/sse");
}

export function getRequestHeaders() {
  const token = getStoredToken();
  const headers = {};
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
}

// ---------------------------------------------------------------------------
// Support
// ---------------------------------------------------------------------------

export function fetchSupportChats({
  page = 1,
  pageSize = 25,
  onlyEscalated,
  onlyOpen,
  userId,
  assignedAdminIds,
} = {}) {
  const query = new URLSearchParams();
  query.append("page", page);
  query.append("pageSize", pageSize);
  if (onlyEscalated != null) query.append("onlyEscalated", onlyEscalated);
  if (onlyOpen != null) query.append("onlyOpen", onlyOpen);
  if (userId) query.append("userId", userId);
  if (Array.isArray(assignedAdminIds)) {
    assignedAdminIds.forEach((adminId) => {
      query.append("adminId", adminId === null ? "null" : adminId);
    });
  }
  const queryString = query.toString();
  return apiFetch(`/support/chats${queryString ? `?${queryString}` : ""}`);
}

export function createSupportChat({ userId, subject, isEscalated } = {}) {
  if (!userId) return Promise.reject(new Error("User id is required"));
  if (!subject) return Promise.reject(new Error("Subject is required"));
  return apiFetch(`/support/chats`, {
    method: "POST",
    body: JSON.stringify({ userId, subject, isEscalated }),
  });
}

export function fetchSupportChat(chatId) {
  if (!chatId) return Promise.reject(new Error("Chat id is required"));
  return apiFetch(`/support/chats/${encodeURIComponent(chatId)}`);
}

export function fetchSupportChatMessages(chatId, { page = 1, pageSize = 30 } = {}) {
  if (!chatId) return Promise.reject(new Error("Chat id is required"));
  return apiFetch(
    `/support/chats/${encodeURIComponent(chatId)}/messages${buildQueryString({ page, pageSize })}`
  );
}

export function submitSupportMessage(chatId, { id, agentName, text, mediaPath }) {
  if (!chatId) return Promise.reject(new Error("Chat id is required"));
  return apiFetch(`/support/chats/${encodeURIComponent(chatId)}/messages`, {
    method: "POST",
    body: JSON.stringify({ id, agentName, text, mediaPath }),
  });
}

export function editSupportMessage(remoteId, text) {
  if (!remoteId) return Promise.reject(new Error("Message remote id is required"));
  return apiFetch(`/support/messages/${encodeURIComponent(remoteId)}`, {
    method: "PUT",
    body: JSON.stringify({ text }),
  });
}

export function setSupportChatEscalated(chatId, isEscalated) {
  if (!chatId) return Promise.reject(new Error("Chat id is required"));
  return apiFetch(`/support/chats/${encodeURIComponent(chatId)}/escalate`, {
    method: "POST",
    body: JSON.stringify({ isEscalated }),
  });
}

export function setSupportChatOpen(chatId, isClosed) {
  if (!chatId) return Promise.reject(new Error("Chat id is required"));
  return apiFetch(`/support/chats/${encodeURIComponent(chatId)}/close`, {
    method: "POST",
    body: JSON.stringify({ isClosed }),
  });
}

export function setSupportChatAssignedAdmin(chatId, adminId) {
  if (!chatId) return Promise.reject(new Error("Chat id is required"));
  return apiFetch(`/support/chats/${encodeURIComponent(chatId)}/assign`, {
    method: "POST",
    body: JSON.stringify({ adminId: adminId || null }),
  });
}

export function fetchSupportAnswerSuggestions({ chatId, userId, adminInput } = {}) {
  if (!chatId && !userId) {
    return Promise.reject(new Error("Either chatId or userId is required"));
  }
  return apiFetch(`/support/suggest-answer`, {
    method: "POST",
    body: JSON.stringify({
      chatId: chatId || null,
      userId: userId || null,
      adminInput: adminInput || null,
    }),
  });
}

export function fetchSupportMessagesTranslation(messageIds, targetLanguage) {
  const ids = Array.isArray(messageIds) ? messageIds : [messageIds];
  if (ids.length === 0) {
    return Promise.reject(new Error("messageIds is required"));
  }
  if (!targetLanguage) {
    return Promise.reject(new Error("Target language is required"));
  }
  const query = new URLSearchParams();
  ids.forEach((value) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    if (trimmed) {
      query.append("messageIds[]", trimmed);
    }
  });
  query.append("targetLanguage", targetLanguage);
  return apiFetch(`/translation/support-messages?${query.toString()}`);
}

// ---------------------------------------------------------------------------
// Email Inbox
// ---------------------------------------------------------------------------

export function fetchEmailMessagesTranslation(messageIds, targetLanguage) {
  const ids = Array.isArray(messageIds) ? messageIds : [messageIds];
  if (ids.length === 0) {
    return Promise.reject(new Error("messageIds is required"));
  }
  if (!targetLanguage) {
    return Promise.reject(new Error("Target language is required"));
  }
  const query = new URLSearchParams();
  ids.forEach((value) => {
    const trimmed = typeof value === "string" ? value.trim() : value;
    if (trimmed) {
      query.append("messageIds[]", trimmed);
    }
  });
  query.append("targetLanguage", targetLanguage);
  return apiFetch(`/translation/email-messages?${query.toString()}`);
}

export function fetchEmailManagementStatus() {
  return apiFetch(`/email-management/status`);
}

export function fetchEmailThreadsPage({
  page = 1,
  pageSize = 25,
  sort = "newest",
  deleted,
  participantEmail,
  assignedAdminIds,
} = {}) {
  const query = new URLSearchParams();
  query.append("page", page);
  query.append("pageSize", pageSize);
  query.append("sort", sort);
  if (deleted != null) query.append("deleted", deleted);
  if (participantEmail) query.append("participantEmail", participantEmail);
  if (Array.isArray(assignedAdminIds)) {
    assignedAdminIds.forEach((adminId) => {
      query.append("adminId", adminId === null ? "null" : adminId);
    });
  }
  const queryString = query.toString();
  return apiFetch(
    `/email-management/threads${queryString ? `?${queryString}` : ""}`
  );
}

export function fetchEmailManagementAdmins() {
  return apiFetch(`/email-management/admins`);
}

export function setEmailThreadAssignees(threadId, adminIds) {
  if (!threadId) return Promise.reject(new Error("threadId is required"));
  return apiFetch(
    `/email-management/threads/${encodeURIComponent(threadId)}/assignees`,
    {
      method: "POST",
      body: JSON.stringify({ adminIds: adminIds || [] }),
    }
  );
}

export function fetchEmailThread(threadId) {
  if (!threadId) return Promise.reject(new Error("threadId is required"));
  return apiFetch(`/email-management/threads/${encodeURIComponent(threadId)}`);
}

export function fetchEmailReplySuggestions(threadId, { adminInput } = {}) {
  if (!threadId) return Promise.reject(new Error("threadId is required"));
  return apiFetch(
    `/email-management/threads/${encodeURIComponent(threadId)}/suggest-reply`,
    {
      method: "POST",
      body: JSON.stringify({ adminInput: adminInput || null }),
    }
  );
}

export function sendEmailReply(threadId, { bodyText, bodyHtml, fromEmail } = {}) {
  if (!threadId) return Promise.reject(new Error("threadId is required"));
  return apiFetch(
    `/email-management/threads/${encodeURIComponent(threadId)}/reply`,
    {
      method: "POST",
      body: JSON.stringify({
        bodyText,
        bodyHtml: bodyHtml || null,
        fromEmail: fromEmail || null,
      }),
    }
  );
}

export function sendNewEmail({
  toEmail,
  toName,
  subject,
  bodyText,
  bodyHtml,
  fromEmail,
} = {}) {
  return apiFetch(`/email-management/threads`, {
    method: "POST",
    body: JSON.stringify({
      toEmail,
      toName: toName || null,
      subject,
      bodyText,
      bodyHtml: bodyHtml || null,
      fromEmail: fromEmail || null,
    }),
  });
}

export function deleteEmailThread(threadId) {
  if (!threadId) return Promise.reject(new Error("threadId is required"));
  return apiFetch(`/email-management/threads/${encodeURIComponent(threadId)}`, {
    method: "DELETE",
  });
}

export function restoreEmailThread(threadId) {
  if (!threadId) return Promise.reject(new Error("threadId is required"));
  return apiFetch(
    `/email-management/threads/${encodeURIComponent(threadId)}/restore`,
    { method: "POST" }
  );
}

export function fetchEmailMessagePayload(threadId, messageId) {
  if (!threadId || !messageId) {
    return Promise.reject(new Error("threadId and messageId are required"));
  }
  return apiFetch(
    `/email-management/threads/${encodeURIComponent(threadId)}` +
      `/messages/${encodeURIComponent(messageId)}/payload`
  );
}

export function fetchEmailAttachment(threadId, messageId, index) {
  if (!threadId || !messageId || index == null) {
    return Promise.reject(
      new Error("threadId, messageId and index are required")
    );
  }
  return apiFetch(
    `/email-management/threads/${encodeURIComponent(threadId)}` +
      `/messages/${encodeURIComponent(messageId)}/attachments/${encodeURIComponent(index)}`,
    { responseType: "blob" }
  );
}

// ---------------------------------------------------------------------------
// Dashboard board
// ---------------------------------------------------------------------------

export function fetchDashboardPosts({ page = 1, pageSize = 30 } = {}) {
  return apiFetch(`/dashboard/post${buildQueryString({ page, pageSize })}`);
}

export function createDashboardPost(text) {
  return apiFetch("/dashboard/post", {
    method: "PUT",
    body: JSON.stringify({ text }),
  });
}

export function updateDashboardPost(postId, text) {
  if (!postId) return Promise.reject(new Error("postId is required"));
  return apiFetch(`/dashboard/post/${encodeURIComponent(postId)}`, {
    method: "POST",
    body: JSON.stringify({ text }),
  });
}

export function deleteDashboardPost(postId) {
  if (!postId) return Promise.reject(new Error("postId is required"));
  return apiFetch(`/dashboard/post/${encodeURIComponent(postId)}`, {
    method: "DELETE",
  });
}

export function createDashboardPostComment(postId, text) {
  if (!postId) return Promise.reject(new Error("postId is required"));
  return apiFetch(`/dashboard/post/${encodeURIComponent(postId)}/comment`, {
    method: "PUT",
    body: JSON.stringify({ text }),
  });
}

export function updateDashboardPostComment(postId, commentId, text) {
  if (!postId || !commentId) {
    return Promise.reject(new Error("postId and commentId are required"));
  }
  return apiFetch(
    `/dashboard/post/${encodeURIComponent(postId)}/comment/${encodeURIComponent(commentId)}`,
    {
      method: "POST",
      body: JSON.stringify({ text }),
    }
  );
}

export function deleteDashboardPostComment(postId, commentId) {
  if (!postId || !commentId) {
    return Promise.reject(new Error("postId and commentId are required"));
  }
  return apiFetch(
    `/dashboard/post/${encodeURIComponent(postId)}/comment/${encodeURIComponent(commentId)}`,
    { method: "DELETE" }
  );
}

export function toggleDashboardPostReaction(postId, emoji) {
  if (!postId) return Promise.reject(new Error("postId is required"));
  return apiFetch(`/dashboard/post/${encodeURIComponent(postId)}/reaction`, {
    method: "POST",
    body: JSON.stringify({ emoji }),
  });
}

export function fetchPaymentMethodPolicies() {
  return apiFetch("/payment-method");
}

export function savePaymentMethodPolicy(method, payload) {
  if (!method) {
    return Promise.reject(new Error("Payment method is required"));
  }
  return apiFetch(`/payment-method/${encodeURIComponent(method)}`, {
    method: "POST",
    body: JSON.stringify(payload || {}),
  });
}

export function evaluatePaymentMethodsForUser(userId) {
  if (!userId) {
    return Promise.reject(new Error("User id is required"));
  }
  return apiFetch(`/payment-method/evaluate/${encodeURIComponent(userId)}`);
}
